import { useEffect } from 'react'
import { getNodeHistoryDetailByVersion } from 'src/endpoints'

import { useApiRequest } from '@/endpoints/hooks'
import { StructureObjectDto } from '@/endpoints/models'
import { TableMode } from '@/enums'
import { useAppDispatch, useAppStore } from '@/hooks'
import { useIsMounted } from '@/hooks/useIsMounted'
import { getFolderDataForm } from '@/store/modules/folder/helpers'
import { updateFormHighlights } from '@/store/modules/formHighlights/actions'
import { getSystemDataForm } from '@/store/modules/system/helpers'

import { useDetailTabContext } from '../components/DetailTab/context/DetailTabContext'
import { getModeForm, getUnsyncedFields } from '../pages/TableDetail/utils'
import { buildViewPrevVersionHighlights } from '../pages/ViewDetailV2/utils/viewHistoryHighlights'

/** Load latest-version snapshot into Redux; highlights show fields that differ from latest while browsing older history. */
export const useFormHighlightsData = () => {
	const isMounted = useIsMounted()
	const dispatch = useAppDispatch()
	const request = useApiRequest()
	const abortController = new AbortController()

	useEffect(() => {
		return () => {
			dispatch(updateFormHighlights({ active: false, prevVersionValues: null }))
			abortController.abort()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const {
		state: { node, version, versionActive, versionMax },
	} = useDetailTabContext()

	const mode = useAppStore(
		(state) => state.table.tables[node.id]?.mode ?? TableMode.TABLE,
	)

	useEffect(() => {
		const call = async () => {
			// Compare selected snapshot (form) to **latest** (versionMax). No highlights on latest row.
			if (
				!versionActive ||
				version == null ||
				versionMax == null ||
				version >= versionMax
			) {
				dispatch(
					updateFormHighlights({ active: false, prevVersionValues: null }),
				)
				return
			}

			const { data: historyDetail } = await request(
				getNodeHistoryDetailByVersion(node.id, versionMax),
			)

			if (!historyDetail?.data) {
				dispatch(
					updateFormHighlights({ active: false, prevVersionValues: null }),
				)
				return
			}

			const rawHistoryJson = historyDetail.data

			let prevVersionValues: Record<string, unknown>

			if (node.type === StructureObjectDto.TypeEnum.VIEW) {
				const mapped = buildViewPrevVersionHighlights(rawHistoryJson)
				if (!mapped) {
					dispatch(
						updateFormHighlights({ active: false, prevVersionValues: null }),
					)
					return
				}
				prevVersionValues = mapped as Record<string, unknown>
			} else {
				try {
					prevVersionValues = JSON.parse(rawHistoryJson)
				} catch {
					dispatch(
						updateFormHighlights({ active: false, prevVersionValues: null }),
					)
					return
				}

				switch (node.type) {
					case StructureObjectDto.TypeEnum.FOLDER: {
						prevVersionValues = getFolderDataForm(prevVersionValues)
						break
					}

					case StructureObjectDto.TypeEnum.SYSTEM: {
						prevVersionValues = getSystemDataForm(prevVersionValues)
						break
					}

					case StructureObjectDto.TypeEnum.TABLE: {
						const unsyncedFields = getUnsyncedFields(mode, prevVersionValues)
						const modeForm = getModeForm(
							mode,
							prevVersionValues,
							unsyncedFields,
						)
						prevVersionValues = modeForm
						break
					}
				}
			}

			if (abortController.signal.aborted) {
				return
			}

			if (isMounted()) {
				dispatch(updateFormHighlights({ active: true, prevVersionValues }))
			}
		}

		call()

		return () => {
			abortController.abort()
		}
	}, [dispatch, mode, node.id, node.type, request, version, versionActive, versionMax, isMounted])
}
